// @flow

import React, { useState, useEffect } from "react";
import * as R from "ramda";
import { useSelector } from "react-redux";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { DndContext, closestCenter } from "@dnd-kit/core";

import FieldSelect from "./FieldSelect";
import { Label, FormGroupSmall, ErrorMessage } from "../styles";
import { AddField } from "./styles";
import Icon from "src/icons";
import SelectProcess from "src/containers/workflow/select";
import { VStack } from "@chakra-ui/react";
import { checkIfProcessDeleted } from "src/utils/workflow";

import { getWorkflow } from "src/reducers";
import { workflowErrors } from "src/constants/errorMessages";

type Props = {
  workflow: number | null,
  fields: Array<number>,
  fieldIds: Array<string>,
  handleTemplate: Function,
  addField: Function,
  handleField: Function,
  removeField: Function,
  checklistFields: Array<Object>,
  settings?: Object,
  setDefaultFields?: Function,
  onSortFields?: Function,
};

const Workflow = ({
  workflow,
  fields,
  fieldIds,
  handleTemplate,
  addField,
  handleField,
  removeField,
  checklistFields = [],
  settings = {},
  setDefaultFields,
  onSortFields,
}: Props) => {
  const processDetails = useSelector(({ app }) =>
    getWorkflow(app, `${workflow || ""}`),
  );

  const isProcessDeleted = checkIfProcessDeleted(processDetails, workflow);

  const [defaultFieldMap, setDefaultFieldMap] = useState({});

  useEffect(() => {
    const updatedDefaultFieldMap = (settings.defaultFields ?? []).reduce(
      (acc, field) => {
        if (field) {
          acc[field] = true;
        }
        return acc;
      },
      {},
    );

    setDefaultFieldMap(updatedDefaultFieldMap);
  }, [settings.defaultFields]);

  useEffect(() => {
    const defaultFieldIds = fields.filter(
      (id) => id != null && defaultFieldMap[id],
    );
    if (setDefaultFields) {
      setDefaultFields(defaultFieldIds);
    }
  }, [defaultFieldMap, fields]);

  const handleDefaultField = (field: number) => {
    setDefaultFieldMap((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleRemoveField = (field: number) => {
    // Remove from defaultFields if present
    if (defaultFieldMap[field]) {
      setDefaultFieldMap((prev) => {
        const newMap = { ...prev };
        delete newMap[field];
        return newMap;
      });
    }

    removeField(field);
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id || !onSortFields) return;
    const oldIndex = fieldIds.indexOf(active.id);
    const newIndex = fieldIds.indexOf(over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      onSortFields({ oldIndex, newIndex });
    }
  };

  return (
    <>
      <FormGroupSmall>
        <Label>Process</Label>
        <SelectProcess workflowId={workflow} handleChange={handleTemplate} />
        {isProcessDeleted && (
          <ErrorMessage>{workflowErrors.PROCESS_DELETED}</ErrorMessage>
        )}
      </FormGroupSmall>

      <div style={{ width: "100%" }}>
        <Label>Embedded Fields</Label>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={fieldIds}
            strategy={verticalListSortingStrategy}
          >
            <VStack alignItems="start">
              {!isProcessDeleted &&
                fields.map((field: ?number, index) => (
                  <FieldSelect
                    id={fieldIds[index]}
                    isDefault={defaultFieldMap[field ?? -1]}
                    itemIndex={index}
                    key={`item-${field ?? -1}-${index}`}
                    handleField={handleField}
                    removeField={handleRemoveField}
                    addField={addField}
                    fields={fields}
                    value={field}
                    checklistFields={checklistFields}
                    handleDefaultField={handleDefaultField}
                    isLinkedField={!R.isEmpty(settings)}
                  />
                ))}
            </VStack>
          </SortableContext>
        </DndContext>
      </div>

      <AddField
        data-cy="add-embedded-field-button"
        type="button"
        disabled={
          !workflow ||
          isProcessDeleted ||
          fields[fields.length - 1] === null ||
          fields.indexOf(null) !== -1
        }
        onClick={addField}
      >
        <Icon type="hollowPlus" />
        <span>Add Embedded Field</span>
      </AddField>
    </>
  );
};

export default Workflow;
